/*
 * Copyright (c) 2020 SQUAREPING INC.
 *
 * SQUAREPING CONFIDENTIAL
 * [2019 and Beyond] SQUAREPING INC. All Rights Reserved.
 *
 * NOTICE: The source code contained herein is, and remains the property of SQUAREPING INC.
 * and its suppliers, if any. The intellectual and technical concepts contained herein are
 * proprietary to SQUAREPING INC. and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 *
 * Dissemination of this information or reproduction of this material is strictly forbidden
 * anywhere in the world unless prior written permission is obtained from SQUAREPING INC.
 */

import { io, Socket } from 'socket.io-client';
import Emitter from '../../modules/Emitter.module';
import { CommonUtil } from '../../utils/common';
import { BroadcastEvent, SocketEvent } from './event';

const LOG_TAG = 'SOCKET';

export class PingSocketError extends Error {
  constructor(public code: number, message?: string) {
    super(message);
  }
}

interface PingSocketResponse<T> {
  resultCode: number;
  errMessage?: string;
  data?: T;
}

const DEFAULT_EMIT_TIMEOUT_MS = 15 * 1000;
const TIMEOUT_CODE = 504;

export enum PingSocketType {
  Location = 1,
  // Messaging = 2,
  Call = 2,
}

export default class PingSocket {
  private socket!: Socket;
  private userId?: string;
  private accessToken?: string;
  private newAccessTokenFunc?: () => Promise<string>;

  private clusterId?: string;
  private serverId?: string;
  private debug = false;

  private isConnecting = false;
  private isLoggingIn = false;
  private _isLoggedIn = false;
  private reconnectCount = 0;
  private lastReceivingTime = 0;
  private lastSendingTime = 0;

  private boundEvents: {[event: string]: Array<(...args: any[]) => void>} = {};

  constructor(private readonly type: PingSocketType, private url: string) {
    this.initSocket(url);
  }

  public setUrl = (url: string): void => {
    this.initSocket(url);
  };

  public setUserId = (userId?: string): void => {
    this.userId = userId;
  };

  public setAccessToken = (accessToken?: string): void => {
    this.accessToken = accessToken;
  };

  public setNewAccessTokenFunc = (func?: () => Promise<string>): void => {
    this.newAccessTokenFunc = func;
  };

  public connect(): void {
    this.socket.connect();
  }

  public disconnect(): void {
    this.socket.disconnect();
  }

  public reconnect(): void {
    if (!this.isConnecting) {
      this.isConnecting = true;
      this.disconnect();
      this.connect();
    }
  }

  public get isConnected(): boolean {
    return this.socket.connected;
  }

  public get isLoggedIn(): boolean {
    return this._isLoggedIn;
  }

  public set isLoggedIn(value: boolean) {
    this._isLoggedIn = value;
  }

  public getServerInfo = (): { clusterId?: string; serverId?: string } => {
    return {
      clusterId: this.clusterId,
      serverId: this.serverId,
    };
  };

  public on = (event: string, listener: (...args: any[]) => void): void => {
    this.boundEvents[event] = this.boundEvents[event] || [];
    this.boundEvents[event].push(listener);
    this.socket.on(event, (data?: any) => {
      this.log('on', { event, data });
      this.lastReceivingTime = Date.now();
      listener(data);
    });
  };

  public off = (event: string, listener?: (...args: any[]) => void): void => {
    this.socket.off(event, listener);
    if (!listener) {
      delete this.boundEvents[event];
    } else {
      this.boundEvents[event] = this.boundEvents[event]?.filter((l) => l !== listener);
    } 
  };

  public checkStatus = (): void => {
    if (!this.isConnected) {
      this.connect();
    }
    if (this.isConnected && !this.isLoggedIn) {
      this.login().catch(console.log);
    }
    this.ping();
  };

  // return true if success; false for timed-out
  public connectAndWaitSuccess = async (timeoutMs?: number): Promise<boolean> => {
    const timestamp = Date.now();
    let isAlive = false;

    // check alive first
    if (this.isConnected) {
      isAlive = await this.ping();
      if (isAlive && this.isLoggedIn) {
        return true;
      }
    }

    // force reconnect if need
    if (!this.isConnecting) {
      this.log('force reconnect due to connectAndWaitSuccess');
      this.isConnecting = true;
      this.reconnect();
    }

    // await for socket connection to be reconnected
    while (!this.isLoggedIn) {
      await CommonUtil.sleep(1000);
      if (timeoutMs && Date.now() - timestamp > timeoutMs) {
        this.log('connectAndWaitSuccess timed out');
        return false; // timed-out
      }
    }

    // reconnected successfully, do ping again
    isAlive = await this.ping();
    if (!isAlive) {
      this.log('connectAndWaitSuccess ping again failed');
    }
    return isAlive;
  };

  public emit = async <T>(event: string, data: unknown, timeoutMs = DEFAULT_EMIT_TIMEOUT_MS): Promise<T> => {
    if (!this.isConnected) {
      this.connect();
    }
    const timestamp = Date.now();

    while (!this.isLoggedIn && ![SocketEvent.Ping, SocketEvent.UserLogin].includes(event as SocketEvent)) {
      await CommonUtil.sleep(1000);
      if (Date.now() - timestamp > timeoutMs) {
        this.log('emit error', { event, data, response: { resultCode: TIMEOUT_CODE, errMessage: 'timeout log in' } });
        Emitter.emit(BroadcastEvent.SocketErrorTimeout, { type: this.type });
        throw new PingSocketError(TIMEOUT_CODE, 'timeout log in');
      }
    }

    this.lastSendingTime = Date.now();
    this.log('emit send', { timestamp, event, data });

    return new Promise((resolve, reject) => {
      let timedOut = false;
      const timeoutId = setTimeout(() => {
        timedOut = true;
        this.log('emit error', { event, data, response: { resultCode: TIMEOUT_CODE, errMessage: 'timeout' } });
        Emitter.emit(BroadcastEvent.SocketErrorTimeout, { type: this.type });
        reject(new PingSocketError(TIMEOUT_CODE, 'timeout'));
      }, timeoutMs);

      this.socket.emit(
        event,
        data,
        async (
          response: PingSocketResponse<T> = {
            resultCode: 500,
            errMessage: 'Unknown response',
          },
        ) => {
          this.lastReceivingTime = Date.now();

          if (timedOut) {
            return; // should ignore ack when setTimeout has been triggered
          }

          clearTimeout(timeoutId);

          if (response.resultCode === 0) {
            this.log('emit ack ', { timestamp, event, response });
            resolve(response.data as T);
            return;
          }

          this.log('emit error', { event, data, response });
          if (response.resultCode !== 401) {
            reject(new PingSocketError(response.resultCode, response.errMessage));
            return;
          }

          this.isLoggedIn = false;
          if (this.newAccessTokenFunc) {
            try {
              this.accessToken = await this.newAccessTokenFunc();
              this.reconnect();
            } catch (err: any) {
              console.warn(err.message);
            }
          }

          reject(new PingSocketError(response.resultCode, response.errMessage));
        },
      );
    });
  };

  private initSocket(url: string) {
    const oldSocket = this.socket;
    this.url = url;

    this.socket = io(this.url, {
      reconnection: true,
      forceNew: true,
      // @ts-ignore
      jsonp: false,
      upgrade: true,
      rememberUpgrade: true,
      transports: ['websocket'],
    });

    this.socket.on('connecting', this.onConnecting);
    this.socket.on('connect', this.onConnect);
    this.socket.on('reconnecting', this.onReconnecting);
    this.socket.on('reconnect', this.onReconnect);
    this.socket.on('disconnect', this.onDisconnect);
    this.socket.on('connect_timeout', this.onConnectTimeout);
    this.socket.on('connect_error', this.onConnectError);
    this.socket.on('reconnect_error', this.onReconnectError);
    this.socket.on('reconnect_failed', this.onReconnectFailed);
    this.socket.on('error', this.onError);
    this.socket.on('ping', this.onPing);
    this.socket.on('pong', this.onPong);

    Object.entries(this.boundEvents).forEach(([event, listeners]) => {
      listeners.forEach((listener) => {
        this.socket.on(event, listener);
      });
    });
    if (oldSocket) {
      oldSocket.removeAllListeners();
      oldSocket.disconnect();
    }
  }

  private onConnecting = async () => {
    this.log('on connecting');
    this.isConnecting = true;
    this.increaseReconnectingCount();
  };

  private onReconnecting = async () => {
    this.log('on reconnecting');
    this.isConnecting = true;
    this.increaseReconnectingCount();
  };

  private onConnect = async () => {
    this.log('on connected');
    await this.onConnected();
    this.isConnecting = false;
    this.reconnectCount = 0;
  };

  private onReconnect = async () => {
    this.log('on reconnected');
    await this.onConnected();
    this.isConnecting = false;
    this.reconnectCount = 0;
  };

  private onDisconnect = async (reason: string) => {
    this.log('on disconnected', { reason });
    this.isLoggedIn = false;
    this.reconnectCount = 0;
    Emitter.emit(BroadcastEvent.SocketDisconnected, { type: this.type, reason });
  };

  private onConnectTimeout = async () => {
    this.log('on connect_timeout');
  };

  private onConnectError = async (err: any) => {
    this.log('on connect_error', JSON.stringify(err));
  };

  private onReconnectError = async (err: { name: string; message: string; description: any }) => {
    this.log('on reconnect_error', err);
  };

  private onReconnectFailed = async () => {
    this.log('on reconnect_failed');
  };

  private onError = async (err: { name: string; message: string; description: any }) => {
    this.log('on error', err);
  };

  private onPing = () => {
    // NOTE: THIS IS NOT SURE THAT PING PACKET IS SENT TO SERVER SUCCESSFULLY
    this.lastSendingTime = Date.now();
    // console.log('on ping', Date.now());
  };

  private onPong = (ms: number) => {
    // ms: number of ms elapsed since ping packet (i.e.: latency).
    this.lastReceivingTime = Date.now();
    // console.log('on pong', Date.now(), ms, this.socket.io.engine.transport.name);
  };

  private increaseReconnectingCount = () => {
    this.reconnectCount += 1;
    if (this.reconnectCount > 3) {
      this.reconnectCount = 0;
      Emitter.emit(BroadcastEvent.SocketReconnectExceed, { type: this.type });
    }
  };

  private onConnected = async () => {
    Emitter.emit(BroadcastEvent.SocketConnected, { type: this.type });
    if (!this.isLoggingIn) {
      this.login().catch(console.log);
    }
  };

  private login = async (): Promise<void> => {
    this.isLoggingIn = true;
    try {
      // console.log('Socket login start', this.userId, this.accessToken);
      if (!this.userId || !this.accessToken) {
        throw new PingSocketError(401, 'User not logged in');
      }
      const response = await this.emit<{ clusterId: string; serverId: string }>(SocketEvent.UserLogin, {
        userId: this.userId,
        accessToken: this.accessToken,
      });
      this.isLoggedIn = true;
      console.log('Socket login successful');
      this.clusterId = response.clusterId;
      this.serverId = response.serverId;
    } catch (err: any) {
      this.isLoggedIn = false;
      this.clusterId = undefined;
      this.serverId = undefined;
      console.error('Socket login error', { error: err.message });
      // throw err;
    } finally {
      this.isLoggingIn = false;
    }
  };

  /**
   * check alive, return true if alive, otherwise false
   */
  private ping = async (): Promise<boolean> => {
    try {
      await this.emit<void>(SocketEvent.Ping, {}, 7 * 1000);
      return true;
    } catch {}
    return false;
  };

  private log = (event = 'general', data?: unknown) => {
    const time = new Date();
    if (this.debug) {
      console.warn(time.toISOString(), `[${LOG_TAG}]`, event, data);
    }
  };
}
